import sizes from "../../design-tokens/sizes";
import { chevronLeft } from "../../js/helpers/built-in-icons";
import { actions, renderState } from "../../js/state-machine";
import { isHTMLElement, isObject, prefix } from "../../js/utilities";

export const componentName = "TabPane";

export const tabPaneClasses = {
    header: `${prefix}tabs__pane__header`,
    content: `${prefix}tabs__pane__content`,
    bodyOverflow: `${prefix}tabs__pane--body-overflow`,
};

const views = {
    body: {
        active: { classList: [tabPaneClasses.bodyOverflow] },
        inactive: { classList: [`!${tabPaneClasses.bodyOverflow}`] },
    },
    active: { attrs: { "aria-hidden": "false", tabindex: "0" } },
    inactive: { attrs: { "aria-hidden": "true", tabindex: "-1" } },
};

export default function TabPane(element, props) {
    if (!isHTMLElement(element)) return console.error(`${componentName} missing element`);
    if (element[componentName]) return console.warn(`${componentName} already initialized`);
    if (!isObject(props)) props = {};

    const handleBackClick = () => {
        const action = actions.setInactiveIndex(index, true);
        tabsStore.dispatch(action);

        previousFocusedTabItem && previousFocusedTabItem.focus();
        previousFocusedTabItem = null;
    };

    const createHeader = () => {
        const headerNode = document.createElement("div");
        headerNode.classList.add(tabPaneClasses.header);

        const backNode = document.createElement("button");
        backNode.textContent = backLabel;
        backNode.setAttribute("type", "button");
        backNode.insertBefore(leftChevronIconSvg, backNode.firstChild);
        backNode.addEventListener("click", handleBackClick);

        headerNode.appendChild(backNode);

        return { headerElement: headerNode, backElement: backNode };
    };

    const createContent = () => {
        const contentNode = document.createElement("div");
        contentNode.setAttribute("tabindex", "0");
        contentNode.classList.add(tabPaneClasses.content);
        return contentNode;
    };

    const createStartFocusTrap = () => {
        const startFocusTrapNode = document.createElement("div");
        startFocusTrapNode.setAttribute("tabindex", "0");
        startFocusTrapNode.setAttribute("aria-hidden", "true");
        startFocusTrapNode.addEventListener("focus", handleStartFocusTrapFocus);
        return startFocusTrapNode;
    };

    const createEndFocusTrap = () => {
        const endFocusTrapNode = document.createElement("div");
        endFocusTrapNode.setAttribute("tabindex", "0");
        endFocusTrapNode.setAttribute("aria-hidden", "true");
        endFocusTrapNode.addEventListener("focus", handleEndFocusTrapFocus);
        return endFocusTrapNode;
    };

    const appendStartFocusTrapToPane = () => {
        element.insertBefore(startFocusTrapElement, element.firstChild);
    };

    const appendEndFocusTrapToPane = () => {
        element.insertBefore(endFocusTrapElement, element.firstChild);
    };

    const appendHeaderToPane = () => {
        element.insertBefore(headerElement, element.firstChild);
    };

    const appendContentToPane = () => {
        element.insertBefore(contentElement, element.firstChild);
    };

    const handleStateChange = (newStateList) => {
        const isActive = newStateList[index];

        const windowInnerWidth = window.innerWidth;
        const isScreenBiggerThanMedium = windowInnerWidth >= mediumBreakpoint;

        if (
            !isScreenBiggerThanMedium &&
            isMobileStacked &&
            isActive &&
            !element.contains(headerElement)
        ) {
            renderState(views.body.active, document.body);
            previousFocusedTabItem = document.activeElement;

            // Append all childNodes from element to new content div
            // This is different than cloneNode or copying the innerHTML, it keeps the event listeners
            contentElement.append(...element.childNodes);

            appendEndFocusTrapToPane();
            appendContentToPane();
            appendHeaderToPane();
            appendStartFocusTrapToPane();
        } else if (element.contains(headerElement)) {
            renderState(views.body.inactive, document.body);

            // Append all childNodes from element to new content div
            // This is different than cloneNode or copying the innerHTML, it keeps the event listeners
            element.append(...contentElement.childNodes);

            headerElement.remove();
            startFocusTrapElement.remove();
            endFocusTrapElement.remove();
            contentElement.remove();
        }

        renderState(isActive ? views.active : views.inactive, element);
        backElement && backElement.focus();
    };

    const handleStartFocusTrapFocus = () => {
        if (previousFocusedElement) {
            previousFocusedElement.focus();
            previousFocusedElement = null;
        } else {
            backElement.focus();
        }
    };

    const handleEndFocusTrapFocus = (e) => {
        previousFocusedElement = e.relatedTarget;
        backElement.focus();
    };

    const dispose = () => {
        const isActive = tabsStore.getState()[index];

        const windowInnerWidth = window.innerWidth;
        const isScreenBiggerThanMedium = windowInnerWidth >= mediumBreakpoint;

        if (!isScreenBiggerThanMedium && isMobileStacked && isActive) {
            renderState(views.inactive, element);
            renderState(views.body.inactive, document.body);
        }

        element[componentName] = null;
        element = null;

        // When the pane in mobile is open
        // and disposed is called remove the elements
        if (headerElement) headerElement.remove();
        if (startFocusTrapElement) startFocusTrapElement.remove();
        if (endFocusTrapElement) endFocusTrapElement.remove();
        if (contentElement) contentElement.remove();

        tabsStore.unsubscribe(handleStateChange);
    };

    const { index, tabsStore, isMobileStacked, backLabel } = props;
    const mediumBreakpoint = parseInt(sizes.breakpoints["breakpoints-md"]()) - 1;

    const leftChevronIconSvg = new DOMParser().parseFromString(chevronLeft(), "text/html").body
        .firstChild;

    const startFocusTrapElement = createStartFocusTrap();
    const endFocusTrapElement = createEndFocusTrap();
    const { headerElement, backElement } = createHeader();
    const contentElement = createContent();

    let previousFocusedTabItem = null;
    let previousFocusedElement = null;

    // Subscribe to main store
    tabsStore.subscribe(handleStateChange);

    const api = {
        dispose,
    };

    element[componentName] = api;

    return api;
}
